import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Typography,
} from "@mui/material";
import * as React from "react";
import { ITableHeaders } from "./table.interface";

/**
 * Which columns a table shows, chosen per user and remembered.
 *
 * Cut down from the one Campaign Stats ships
 * (`Report/CampaignStatsBeta/ColumnPickerDialog.tsx`): same idea — locked core
 * fields, everything else toggleable, reset to defaults — without the Beeswax
 * field catalogue, since here the columns are just the header array. A popover
 * rather than a dialog because these tables have a dozen columns, not a few
 * hundred.
 *
 * Rendered by `CustomTable`, which also decides what is locked. Two things are
 * never hideable, and the second is an invariant rather than a nicety:
 *
 * - The `grow` column. It is what absorbs the table's leftover width; hide it
 *   and `growIndex` is -1, the slack falls back to the pinned action column, and
 *   it renders at nearly twice its declared width with the buttons stranded at
 *   the left of it.
 * - The action column, which is how you act on a row.
 *
 * The list it renders is the *permitted* headers, not the visible ones —
 * otherwise a hidden column would disappear from its own picker and could never
 * be brought back.
 */
export default function ColumnPicker({
  headers,
  hidden,
  onChange,
  lockedIds,
  sortedHiddenColumn,
  onClearSort,
}: {
  headers: ITableHeaders[];
  hidden: string[];
  onChange: (hidden: string[]) => void;
  lockedIds: string[];
  /** The header the table is sorted by, when that column is not on screen. */
  sortedHiddenColumn?: ITableHeaders;
  onClearSort?: () => void;
}) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  // What is actually hidden on screen, which is not the same as what the record
  // says. The record belongs to the whole table id and can name columns this
  // render does not have — an id renamed or dropped in a release, or a header
  // array that varies by user — and CustomTable force-shows locked ids whatever
  // the record claims. Counting the record whole made "Columns (8/9)" appear
  // over nine ticked boxes, and could reach zero or go negative.
  const hiddenHere = hidden.filter(
    (id) =>
      headers.some((header) => header.id === id) && !lockedIds.includes(id)
  );
  const shown = headers.length - hiddenHere.length;
  // What "Show all" would actually remove — locked ids included. A locked id can
  // be in the record (a column that gained `grow` in a later release, after a
  // user had already hidden it): CustomTable force-shows it and its checkbox is
  // drawn ticked and disabled, so `toggle` cannot reach it. If the button is
  // gated on `hiddenHere`, which excludes locked ids, then a record holding
  // ONLY such an id has no way out at all — and the day that column stops being
  // `grow` it disappears again with no user action behind it.
  const clearable = hidden.filter((id) =>
    headers.some((header) => header.id === id)
  );

  const toggle = (id: string) =>
    onChange(
      hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id]
    );

  // Clears only what this table can see. `onChange([])` would also throw away a
  // choice made against a different shape of the same list, which the user would
  // have no way of knowing they had just discarded.
  const showAll = () =>
    onChange(
      hidden.filter((id) => !headers.some((header) => header.id === id))
    );

  return (
    <>
      <CustomLoadingButton
        size="small"
        variant="outlined"
        Icon={ViewColumnIcon}
        iconLocation="startIcon"
        isLoading={false}
        title={`Columns (${shown}/${headers.length})`}
        onClick={(event) => setAnchor(event.currentTarget)}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography variant="subtitle2">Show columns</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Hiding a column gives the rest more room.
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {headers.map((header) => {
              const locked = lockedIds.includes(header.id);
              return (
                <FormControlLabel
                  key={header.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={locked || !hidden.includes(header.id)}
                      disabled={locked}
                      onChange={() => toggle(header.id)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {header.label || header.id}
                    </Typography>
                  }
                />
              );
            })}
          </Box>
          <Divider sx={{ my: 1 }} />
          {/* The only place a sort by a hidden column can be undone. Hiding a
              column deliberately does NOT drop the sort — reordering the page
              because someone removed a column they weren't reading is the worse
              surprise, and it silently threw away work they had asked for. But
              that leaves rows ordered by a field with no header on screen and
              no control to clear it, since the sort label went with the column.
              This is that control. */}
          {sortedHiddenColumn && (
            <>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Still sorted by {sortedHiddenColumn.label}, which is hidden.
              </Typography>
              <Button size="small" onClick={onClearSort}>
                Clear sort
              </Button>
              <Divider sx={{ my: 1 }} />
            </>
          )}
          <Button
            size="small"
            disabled={clearable.length === 0}
            onClick={showAll}
          >
            Show all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
